import { useEffect, useMemo } from 'react';

import { useQueryParams } from './useQueryParams';
import { useTimeZonedFunctions } from './useTimeZonedFunctions';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';
import { MAX_ALLOWABLE_DATE_RANGES } from '/src/features/_global/constants';

const UNSUPPORTED_LEGACY_DATE_QUERY_PARAMS = ['date_range', 'start_date', 'end_date'];

export function useCanonicalDateRangeParam({
  timeZone,
  featureStartedAt,
  featureEndedAt,
  lowerBoundary,
  upperBoundary,
  maxAllowableRange = MAX_ALLOWABLE_DATE_RANGES.DEFAULT,
  resolverOptions,
  defaultDateRangeOptions,
  onDateRangeCanonicalized,
} = {}) {
  const { getRaw: getRawParam, hasRaw: hasRawParam, setAll: setAllParams } = useQueryParams();
  const { getDefaultDateRange, resolveDateRangeParam } = useTimeZonedFunctions(timeZone);
  const { activeDefault, endedDefault } = defaultDateRangeOptions ?? {};
  const {
    relativeTo,
    featureStartedAt: resolverFeatureStartedAt,
    featureEndedAt: resolverFeatureEndedAt,
  } = resolverOptions ?? {};

  const rawDateRange = getRawParam('date');
  const hasUnsupportedLegacyDateParams = useMemo(
    () => UNSUPPORTED_LEGACY_DATE_QUERY_PARAMS.some((key) => hasRawParam(key)),
    [hasRawParam],
  );
  const normalizedResolverOptions = useMemo(() => {
    if (relativeTo == null && resolverFeatureStartedAt == null && resolverFeatureEndedAt == null) {
      return undefined;
    }

    return {
      relativeTo,
      featureStartedAt: resolverFeatureStartedAt,
      featureEndedAt: resolverFeatureEndedAt,
    };
  }, [relativeTo, resolverFeatureEndedAt, resolverFeatureStartedAt]);
  const defaultDateRange = useMemo(
    () =>
      getDefaultDateRange({
        featureStartedAt,
        featureEndedAt,
        activeDefault,
        endedDefault,
      }),
    [activeDefault, endedDefault, featureEndedAt, featureStartedAt, getDefaultDateRange],
  );
  const resolvedDateRange = useMemo(
    () =>
      resolveDateRangeParam({
        rawValue: rawDateRange,
        adapter: JSON_DATE_RANGE_ADAPTER,
        defaultDateRange,
        featureStartedAt,
        featureEndedAt,
        lowerBoundary,
        upperBoundary,
        maxAllowableRange,
        resolverOptions: normalizedResolverOptions,
      }),
    [
      defaultDateRange,
      featureEndedAt,
      featureStartedAt,
      lowerBoundary,
      maxAllowableRange,
      normalizedResolverOptions,
      rawDateRange,
      resolveDateRangeParam,
      upperBoundary,
    ],
  );

  useEffect(() => {
    if (!resolvedDateRange.shouldRewrite && !hasUnsupportedLegacyDateParams) {
      return;
    }

    const updates = Object.fromEntries(UNSUPPORTED_LEGACY_DATE_QUERY_PARAMS.map((key) => [key, null]));

    if (resolvedDateRange.shouldRewrite) {
      updates.date = resolvedDateRange.serializedValue;
    }

    setAllParams(updates);

    if (onDateRangeCanonicalized && resolvedDateRange.selectedDateRange) {
      onDateRangeCanonicalized(resolvedDateRange.selectedDateRange);
    }
  }, [
    hasUnsupportedLegacyDateParams,
    onDateRangeCanonicalized,
    resolvedDateRange.selectedDateRange,
    resolvedDateRange.serializedValue,
    resolvedDateRange.shouldRewrite,
    setAllParams,
  ]);

  return {
    defaultDateRange,
    rawDateRange,
    resolvedDateRange,
  };
}
